"use client";

import type { ChangelogMedia } from "@/lib/changelog";
import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * A looping product capture that the reader controls: it plays while in
 * view, stays on its poster under reduced motion, and a manual pause holds
 * until the reader presses play again.
 */
export function ChangelogVideo({ media }: { media: ChangelogMedia }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pausedByReader = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      pausedByReader.current = true;
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !pausedByReader.current) {
          void video.play().catch(() => {});
        } else if (!entry?.isIntersecting) {
          video.pause();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      pausedByReader.current = false;
      void video.play().catch(() => {});
    } else {
      pausedByReader.current = true;
      video.pause();
    }
  };

  return (
    // A recording of the product takes a product shot's edge (one-system's --frame-radius and --frame-edge).
    <figure
      data-frame-corners=""
      className="relative mt-6 overflow-hidden rounded-[var(--frame-radius,8px)] border border-[var(--frame-edge,hsl(220_10%_88%))]"
    >
      <video
        ref={videoRef}
        className="block w-full"
        style={{ aspectRatio: media.aspect ?? "16 / 10" }}
        src={media.src}
        poster={media.poster}
        muted
        loop
        playsInline
        preload="none"
        aria-label={media.alt}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause video" : "Play video"}
        className="absolute right-3 bottom-3 inline-flex size-11 items-center justify-center text-[var(--theme-ground)] transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-ground)]"
        style={{ backgroundColor: "color-mix(in srgb, var(--theme-ink) 72%, transparent)" }}
      >
        {playing ? <Pause aria-hidden="true" className="size-4" /> : <Play aria-hidden="true" className="size-4" />}
      </button>
    </figure>
  );
}
